import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  AppScrollView,
  AppShell,
  AppViewport,
  HomeframeErrorBoundary,
  HomeframeInput,
  HomeframeProvider,
  SelectableText,
  HomeframeRouterProvider,
  RouterOutlet,
  createHomeframeRouter,
  useNavigate,
  useRouterSnapshot,
  useRouteScrollRestoration,
} from '@builtbyted/homeframe';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import LogoutIcon from '@mui/icons-material/Logout';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import { api, ApiError, type AuthStatus, type Item } from './lib/api.js';

const appBase = import.meta.env.BASE_URL.endsWith('/')
  ? import.meta.env.BASE_URL
  : `${import.meta.env.BASE_URL}/`;
const appPath = (path = '/') => path === '/' ? appBase : `${appBase}${path.replace(/^\/+/, '')}`;

const router = createHomeframeRouter([
  { id: 'home', path: appPath(), element: <HomePage /> },
  { id: 'signup', path: appPath('/signup'), element: <SignupPage /> },
  { id: 'login', path: appPath('/login'), element: <LoginPage /> },
  { id: 'not-found', path: '*', element: <NotFoundPage /> },
]);

/* ---------- MUI theme ---------- */

const BRAND = {
  green: '#15803D',
  greenDark: '#166534',
  greenLight: '#4ade80',
  light: { bg: '#F7F6F2', surface: '#FFFFFF', text: '#1A241C', muted: '#5C6B5F', border: '#E2E4DE', danger: '#B3261E' },
  dark: { bg: '#0E1512', surface: '#16201A', text: '#E8EFE9', muted: '#9AA89D', border: '#263129', danger: '#F2A49E' },
};

const FONT_STACK = '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, "Helvetica Neue", sans-serif';

function buildTheme(mode: 'light' | 'dark') {
  const palette = mode === 'dark' ? BRAND.dark : BRAND.light;
  return createTheme({
    palette: {
      mode,
      primary: {
        main: mode === 'dark' ? BRAND.greenLight : BRAND.green,
        dark: BRAND.greenDark,
        light: BRAND.greenLight,
        contrastText: mode === 'dark' ? '#08130C' : '#FFFFFF',
      },
      background: { default: palette.bg, paper: palette.surface },
      text: { primary: palette.text, secondary: palette.muted },
      divider: palette.border,
      error: { main: palette.danger },
    },
    typography: { fontFamily: FONT_STACK },
    shape: { borderRadius: 12 },
  });
}

function MuiRoot({ children }: { children: ReactNode }) {
  const dark = useMediaQuery('(prefers-color-scheme: dark)');
  const theme = useMemo(() => buildTheme(dark ? 'dark' : 'light'), [dark]);
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}

/* ---------- App root ---------- */

export function App() {
  return (
    <HomeframeProvider>
      <HomeframeRouterProvider router={router}>
        <MuiRoot>
          <HomeframeErrorBoundary fallback={(error, retry) => (
            <AppViewport className="fatal-screen">
              <main className="page">
                <Typography variant="h5" component="h1">Something went wrong</Typography>
                <SelectableText>{error.message}</SelectableText>
                <Button variant="contained" onClick={retry}>Try again</Button>
              </main>
            </AppViewport>
          )}>
            <ApplicationShell />
          </HomeframeErrorBoundary>
        </MuiRoot>
      </HomeframeRouterProvider>
    </HomeframeProvider>
  );
}

/* ---------- App store ---------- */

interface AppStore {
  status: AuthStatus | null;
  items: Item[];
  busy: boolean;
  error: string | null;
  refreshStatus(): Promise<void>;
  loadItems(): Promise<void>;
  addItem(name: string): Promise<boolean>;
  toggleItem(item: Item): Promise<void>;
  deleteItem(item: Item): Promise<void>;
  moveItem(index: number, direction: -1 | 1): Promise<void>;
  clearChecked(): Promise<void>;
}

const AppStoreContext = createContext<AppStore | null>(null);

function useAppStore(): AppStore {
  const store = useContext(AppStoreContext);
  if (!store) throw new Error('AppStore is not available');
  return store;
}

function ApplicationShell() {
  const { scrollKey, direction, scrollBehavior, permalinkScroll } = useRouteScrollRestoration();
  const [status, setStatus] = useState<AuthStatus | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshStatus = useCallback(async () => {
    try {
      setStatus(await api.status());
    } catch {
      setStatus({ hasAccount: false, sessionValid: false });
    }
  }, []);

  const loadItems = useCallback(async () => {
    try {
      setItems((await api.items()).items);
      setError(null);
    } catch (err) {
      if (err instanceof Error && err.name === 'ApiError' && err.message === 'unauthorized') {
        setItems([]);
      } else {
        setError(err instanceof Error ? err.message : 'Could not load the list');
      }
    }
  }, []);

  useEffect(() => {
    void refreshStatus();
  }, [refreshStatus]);

  const run = useCallback(async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  }, []);

  const addItem = useCallback(
    async (name: string): Promise<boolean> => {
      const trimmed = name.trim();
      if (trimmed.length === 0) return false;
      await run(async () => {
        const { item } = await api.addItem(trimmed);
        setItems((current) => [...current, item]);
      });
      return true;
    },
    [run],
  );

  const toggleItem = useCallback(
    async (item: Item) => {
      await run(async () => {
        const { item: updated } = await api.updateItem(item.id, { checked: !item.checked });
        setItems((current) => current.map((entry) => (entry.id === updated.id ? updated : entry)));
      });
    },
    [run],
  );

  const deleteItem = useCallback(
    async (item: Item) => {
      await run(async () => {
        await api.deleteItem(item.id);
        setItems((current) => current.filter((entry) => entry.id !== item.id));
      });
    },
    [run],
  );

  const moveItem = useCallback(
    async (index: number, directionValue: -1 | 1) => {
      const target = index + directionValue;
      if (target < 0 || target >= items.length) return;
      const next = [...items];
      const [moved] = next.splice(index, 1);
      if (!moved) return;
      next.splice(target, 0, moved);
      setItems(next);
      await run(async () => {
        await api.reorder(next.map((entry) => entry.id));
      });
    },
    [items, run],
  );

  const clearChecked = useCallback(async () => {
    await run(async () => {
      await api.clearChecked();
      setItems((current) => current.filter((entry) => !entry.checked));
    });
  }, [run]);

  const store = useMemo<AppStore>(
    () => ({ status, items, busy, error, refreshStatus, loadItems, addItem, toggleItem, deleteItem, moveItem, clearChecked }),
    [status, items, busy, error, refreshStatus, loadItems, addItem, toggleItem, deleteItem, moveItem, clearChecked],
  );

  return (
    <AppStoreContext.Provider value={store}>
      <AppViewport className="app-viewport">
        <AppShell header={<Header />} bottom={<BottomDock />}>
          <AppScrollView
            className="page-scroll"
            scrollKey={scrollKey}
            navigationType={direction}
            scrollBehavior={scrollBehavior}
            permalinkScroll={permalinkScroll}
          >
            <RouterOutlet />
          </AppScrollView>
        </AppShell>
      </AppViewport>
    </AppStoreContext.Provider>
  );
}

/* ---------- Chrome ---------- */

function Header() {
  const store = useAppStore();

  async function signOut() {
    try {
      await api.logout();
    } catch {
      // Cookie is cleared client-side regardless.
    }
    await store.refreshStatus();
  }

  return (
    <header className="top-bar">
      <span className="brand-mark" aria-hidden="true">
        <svg viewBox="0 0 1024 1024" width="28" height="28" aria-hidden="true">
          <rect width="1024" height="1024" fill="#15803D" />
          <path d="M402 470 Q512 330 622 470" fill="none" stroke="#FFFFFF" strokeWidth="58" strokeLinecap="round" />
          <path d="M296 470 H728 L676 742 Q668 792 618 792 H406 Q356 792 348 742 Z" fill="#FFFFFF" />
          <g stroke="#15803D" strokeWidth="44" strokeLinecap="round">
            <line x1="436" y1="540" x2="452" y2="724" />
            <line x1="512" y1="540" x2="512" y2="724" />
            <line x1="588" y1="540" x2="572" y2="724" />
          </g>
        </svg>
      </span>
      <Typography variant="subtitle1" sx={{ fontSize: '1.0625rem', fontWeight: 600 }}>Groceries</Typography>
      {store.status?.sessionValid ? (
        <IconButton size="small" sx={{ ml: 'auto' }} onClick={() => void signOut()} aria-label="Sign out">
          <LogoutIcon fontSize="small" />
        </IconButton>
      ) : null}
    </header>
  );
}

// The AppShell `bottom` slot is already wrapped by Homeframe's keyboard-aware
// dock (config: viewport.bottomDock = "avoid"). Nesting another dock here would
// apply the keyboard offset twice, flinging the composer off-screen when the
// keyboard opens. Pass plain content; the shell dock owns the motion.
function BottomDock() {
  const store = useAppStore();
  const navigate = useNavigate();
  if (!store.status) return null;
  if (store.status.sessionValid) {
    return <Composer />;
  }
  const hasAccount = store.status.hasAccount;
  return (
    <div className="dock-prompt">
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {hasAccount ? 'Sign in to see the list' : 'No account yet'}
      </Typography>
      <Button
        size="small"
        variant="contained"
        onClick={() => navigate(appPath(hasAccount ? '/login' : '/signup'))}
      >
        {hasAccount ? 'Sign in' : 'Open signup'}
      </Button>
    </div>
  );
}

function Composer() {
  const store = useAppStore();
  const [draft, setDraft] = useState('');

  const submit = async () => {
    if (store.busy) return;
    const added = await store.addItem(draft);
    if (added) setDraft('');
  };

  return (
    <form
      className="composer"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <HomeframeInput
        className="composer-input"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        placeholder="Add an item…"
        aria-label="New grocery item"
        enterKeyHint="done"
      />
      <Button
        type="submit"
        variant="contained"
        startIcon={<AddIcon />}
        disabled={store.busy || draft.trim().length === 0}
      >
        Add
      </Button>
    </form>
  );
}

/* ---------- Shared page pieces ---------- */

function authErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    switch (err.message) {
      case 'invalid-token':
        return 'This signup link isn\'t valid. Check that the whole link was copied, or ask for a fresh one.';
      case 'invalid-username':
        return 'Choose a username — 1 to 32 characters, no spaces.';
      case 'weak-password':
        return 'The password must be at least 8 characters.';
      case 'username-taken':
        return 'That username is already taken.';
      case 'bad-credentials':
        return 'Wrong username or password.';
      case 'rate-limited':
        return 'Too many attempts. Wait a minute and try again.';
      case 'already-set-up':
        return 'An account already exists for this list.';
      case 'no-account':
        return 'No account exists yet. Open the one-time signup link first.';
    }
  }
  return fallback;
}

function Page({ eyebrow, title, children }: { eyebrow: string; title: string; children: ReactNode }) {
  return (
    <main className="page">
      <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 600, letterSpacing: '0.08em' }}>
        {eyebrow}
      </Typography>
      {/* iOS-style large title: 34px bold, not Material's thin 24px h5. */}
      <Typography variant="h4" component="h1" sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
        {title}
      </Typography>
      {children}
    </main>
  );
}

function AuthField({
  id,
  label,
  type = 'text',
  value,
  onChange,
  placeholder,
  autoComplete,
  isPassword = false,
}: {
  id: string;
  label: string;
  type?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  autoComplete: string;
  isPassword?: boolean;
}) {
  return (
    <Box>
      <Typography component="label" htmlFor={id} sx={{ display: 'block', mb: 0.5, fontWeight: 600, color: 'text.secondary' }}>
        {label}
      </Typography>
      <HomeframeInput
        id={id}
        className="field-input"
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        autoCapitalize={isPassword ? undefined : 'none'}
        autoCorrect="off"
        spellCheck={false}
        maxLength={128}
        required
      />
    </Box>
  );
}

/* ---------- Routes ---------- */

function HomePage() {
  const store = useAppStore();
  const navigate = useNavigate();
  const sessionValid = store.status?.sessionValid ?? false;

  useEffect(() => {
    if (sessionValid) void store.loadItems();
    // loadItems is stable; only reload when the session state flips.
  }, [sessionValid, store.loadItems]);

  if (!store.status) {
    return (
      <Page eyebrow="Groceries" title="Loading…">
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', mt: 2 }}>
          <CircularProgress size={20} />
          <Typography sx={{ color: 'text.secondary' }}>Checking your session.</Typography>
        </Stack>
      </Page>
    );
  }

  if (!store.status.sessionValid) {
    return (
      <Page eyebrow="Groceries" title={store.status.hasAccount ? 'Welcome back' : 'Set up your list'}>
        <Card elevation={0} sx={{ mt: 2, border: 1, borderColor: 'divider' }}>
          <CardContent>
            <Stack spacing={2}>
              <Typography>
                {store.status.hasAccount
                  ? 'Sign in to open the household list.'
                  : 'No account exists yet. Open the one-time signup link to create the account.'}
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate(appPath(store.status?.hasAccount ? '/login' : '/signup'))}
              >
                {store.status.hasAccount ? 'Sign in' : 'Open signup'}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Page>
    );
  }

  const checkedCount = store.items.filter((item) => item.checked).length;

  return (
    <Page eyebrow="Groceries" title="Shopping list">
      {store.error ? (
        <Alert severity="error">{store.error}</Alert>
      ) : null}
      {store.items.length === 0 ? (
        <Card elevation={0} sx={{ mt: 2, border: 1, borderColor: 'divider', textAlign: 'center' }}>
          <CardContent>
            <ShoppingBasketIcon sx={{ fontSize: 48, color: 'primary.main' }} />
            <Typography sx={{ mt: 1, color: 'text.secondary' }}>
              The list is empty. Add your first item below.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Paper elevation={0} sx={{ mt: 2, border: 1, borderColor: 'divider' }}>
          <List disablePadding>
            {store.items.map((item, index) => (
              <ListItem
                key={item.id}
                divider={index < store.items.length - 1}
                sx={{ alignItems: 'center', gap: 0.5, px: 1.5, minHeight: 56 }}
              >
                <Checkbox
                  color="primary"
                  checked={item.checked}
                  onChange={() => void store.toggleItem(item)}
                  slotProps={{ input: { 'aria-label': `${item.checked ? 'Uncheck' : 'Check'} ${item.name}` } }}
                />
                <Box
                  component="span"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    overflowWrap: 'anywhere',
                    textDecoration: item.checked ? 'line-through' : 'none',
                    color: item.checked ? 'text.secondary' : 'text.primary',
                  }}
                >
                  {item.name}
                </Box>
                <IconButton
                  size="small"
                  aria-label={`Move ${item.name} up`}
                  disabled={index === 0 || store.busy}
                  onClick={() => void store.moveItem(index, -1)}
                >
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label={`Move ${item.name} down`}
                  disabled={index === store.items.length - 1 || store.busy}
                  onClick={() => void store.moveItem(index, 1)}
                >
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  edge="end"
                  aria-label={`Delete ${item.name}`}
                  disabled={store.busy}
                  onClick={() => void store.deleteItem(item)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
      {checkedCount > 0 ? (
        <Button
          variant="outlined"
          color="primary"
          fullWidth
          startIcon={<DeleteSweepIcon />}
          disabled={store.busy}
          onClick={() => void store.clearChecked()}
          sx={{ mt: 2 }}
        >
          Clear {checkedCount} checked item{checkedCount === 1 ? '' : 's'}
        </Button>
      ) : null}
    </Page>
  );
}

function SignupPage() {
  const store = useAppStore();
  const navigate = useNavigate();
  const snapshot = useRouterSnapshot();
  const token = snapshot.url.searchParams.get('token') ?? '';

  const [phase, setPhase] = useState<'loading' | 'ready' | 'exists' | 'no-token' | 'rate-limited'>('loading');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Synchronous guard: the `working` state flips asynchronously, so a fast
  // double-tap could submit twice before the button disables. A ref set in the
  // same tick makes the second call a no-op.
  const inFlight = useRef(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!token) {
        setPhase('no-token');
        return;
      }
      try {
        const result = await api.checkSignup(token);
        if (cancelled) return;
        setPhase(result.hasAccount ? 'exists' : 'ready');
      } catch (err) {
        if (cancelled) return;
        // A rate limit is transient — a truncated or unknown token is not the
        // same thing and gets its own (honest) message.
        if (err instanceof ApiError && err.status === 429) setPhase('rate-limited');
        else setPhase('no-token');
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [token, reloadKey]);

  const createAccount = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setWorking(true);
    setError(null);
    try {
      await api.signup(token, username, password);
      await store.refreshStatus();
      navigate(appPath());
    } catch (err) {
      if (err instanceof ApiError && err.message === 'already-set-up') {
        setPhase('exists');
      } else {
        setError(authErrorMessage(err, 'Account creation failed'));
      }
    } finally {
      inFlight.current = false;
      setWorking(false);
    }
  };

  return (
    <Page eyebrow="Signup" title={phase === 'exists' ? 'Already set up — sign in' : 'Create your account'}>
      {phase === 'loading' ? (
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', mt: 2 }}>
          <CircularProgress size={20} />
          <Typography sx={{ color: 'text.secondary' }}>Checking the signup link…</Typography>
        </Stack>
      ) : null}
      {phase === 'no-token' ? (
        <Card elevation={0} sx={{ mt: 2, border: 1, borderColor: 'divider' }}>
          <CardContent>
            <Typography>
              This signup link isn&apos;t valid. Check that the <em>entire</em> link was copied
              (truncated links are rejected) and that it hasn&apos;t been used yet. If it still
              doesn&apos;t work, ask for a fresh link.
            </Typography>
          </CardContent>
        </Card>
      ) : null}
      {phase === 'rate-limited' ? (
        <Card elevation={0} sx={{ mt: 2, border: 1, borderColor: 'divider' }}>
          <CardContent>
            <Stack spacing={2}>
              <Typography>Too many attempts in a short time. Wait a minute, then try again.</Typography>
              <Button variant="contained" onClick={() => setReloadKey((key) => key + 1)}>
                Try again
              </Button>
            </Stack>
          </CardContent>
        </Card>
      ) : null}
      {phase === 'exists' ? (
        <Card elevation={0} sx={{ mt: 2, border: 1, borderColor: 'divider' }}>
          <CardContent>
            <Stack spacing={2}>
              <Typography>An account already exists for this list.</Typography>
              <Button variant="contained" onClick={() => navigate(appPath('/login'))}>
                Sign in
              </Button>
            </Stack>
          </CardContent>
        </Card>
      ) : null}
      {phase === 'ready' ? (
        <form
          className="auth-form"
          onSubmit={(event) => {
            event.preventDefault();
            void createAccount();
          }}
        >
          <Card elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
            <CardContent>
              <Stack spacing={2}>
                <Typography>This one-time link sets the username and password for the household list.</Typography>
                <AuthField
                  id="signup-username"
                  label="Username"
                  value={username}
                  onChange={setUsername}
                  placeholder="e.g. ted"
                  autoComplete="username"
                />
                <AuthField
                  id="signup-password"
                  label="Password"
                  type="password"
                  value={password}
                  onChange={setPassword}
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                  isPassword
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={working || username.trim().length === 0 || password.length < 8}
                >
                  {working ? 'Creating account…' : 'Create account'}
                </Button>
              </Stack>
            </CardContent>
          </Card>
          {error ? (
            <Alert severity="error">{error}</Alert>
          ) : null}
        </form>
      ) : null}
    </Page>
  );
}

function LoginPage() {
  const store = useAppStore();
  const navigate = useNavigate();
  const status = store.status;

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Synchronous guard against a fast double-tap submitting twice.
  const inFlight = useRef(false);

  const signIn = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setWorking(true);
    setError(null);
    try {
      await api.login(username, password);
      await store.refreshStatus();
      navigate(appPath());
    } catch (err) {
      setError(authErrorMessage(err, 'Sign-in failed'));
    } finally {
      inFlight.current = false;
      setWorking(false);
    }
  };

  if (!status) {
    return (
      <Page eyebrow="Sign in" title="Welcome back">
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', mt: 2 }}>
          <CircularProgress size={20} />
          <Typography sx={{ color: 'text.secondary' }}>Checking…</Typography>
        </Stack>
      </Page>
    );
  }

  if (!status.hasAccount) {
    return (
      <Page eyebrow="Sign in" title="Welcome back">
        <Card elevation={0} sx={{ mt: 2, border: 1, borderColor: 'divider' }}>
          <CardContent>
            <Typography>No account exists yet. Open the one-time signup link first.</Typography>
          </CardContent>
        </Card>
      </Page>
    );
  }

  return (
    <Page eyebrow="Sign in" title="Welcome back">
      <form
        className="auth-form"
        onSubmit={(event) => {
          event.preventDefault();
          void signIn();
        }}
      >
        <Card elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
          <CardContent>
            <Stack spacing={2}>
              <Typography>Sign in with your username and password.</Typography>
              <AuthField
                id="login-username"
                label="Username"
                value={username}
                onChange={setUsername}
                placeholder="e.g. ted"
                autoComplete="username"
              />
              <AuthField
                id="login-password"
                label="Password"
                type="password"
                value={password}
                onChange={setPassword}
                placeholder="Your password"
                autoComplete="current-password"
                isPassword
              />
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={working || username.trim().length === 0 || password.length === 0}
              >
                {working ? 'Signing in…' : 'Sign in'}
              </Button>
            </Stack>
          </CardContent>
        </Card>
        {error ? (
          <Alert severity="error">{error}</Alert>
        ) : null}
      </form>
    </Page>
  );
}

function NotFoundPage() {
  const navigate = useNavigate();
  return (
    <Page eyebrow="404" title="That page does not exist.">
      <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate(appPath())}>
        Return home
      </Button>
    </Page>
  );
}
